import React, { useState, useCallback } from "react";
import { api, extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import { FileQuestion, Loader2, CheckCircle2, X, Clock } from "lucide-react";
// Relative on purpose: no "@" alias on the Jest side (cf. admin/LanguageAwareField.jsx).
import useAdminLoad, { payloadShapeError } from "../hooks/useAdminLoad";
import LoadFailure from "../components/ui/LoadFailure";

const STATUS_STYLE = {
  pending_review: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-rose-100 text-rose-800",
};

const STATUS_KEY = {
  pending_review: "qr.statusPending",
  approved: "qr.statusApproved",
  rejected: "qr.statusRejected",
};

/**
 * BUG-066 — an empty list means something DIFFERENT on each tab, so each tab gets its own
 * sentence. One shared `qr.empty` ("No long answers to review") was shown on all four,
 * and on Rejected it contradicted the counts printed directly above it.
 *
 * Keyed by the filter value itself, exactly like STATUS_KEY, so adding a fifth filter
 * without its sentence is a missing key a developer trips over rather than a wrong
 * statement a practitioner reads.
 */
const EMPTY_KEY = {
  pending_review: "qr.empty.pending",
  approved: "qr.empty.approved",
  rejected: "qr.empty.rejected",
  all: "qr.empty.all",
};

/**
 * One review item (a quiz LESSON inside a course) mapped to what its card shows and where
 * a decision is sent. Pathway quiz steps are MCQ-only since pathways v3, so course lessons
 * are the one source of open answers.
 *
 * @returns {{ key: string, heading: string, partLabelKey: string, partTitle: string, markPath: string }}
 */
export function reviewTarget(item) {
  return {
    key: `course-${item.enrollment_id}-${item.lesson_id}`,
    heading: item.course_title,
    partLabelKey: "qr.lesson",
    partTitle: item.lesson_title,
    markPath: `/learn/quiz-reviews/${encodeURIComponent(item.enrollment_id)}/${encodeURIComponent(item.lesson_id)}/mark`,
  };
}

/**
 * QuizReviewsAdmin — the practitioner page for reviewing long quiz answers.
 * Route : /admin/quiz-reviews
 */
export default function QuizReviewsAdmin() {
  const t = useT("admin");
  const tv = useT("common");
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending_review");

  // Stable identity: `useAdminLoad` uses it as its effect dependency, and mark() below
  // shares the very same reader to refresh the list.
  const load = useCallback(async () => {
    const { data } = await api.get("/learn/quiz-reviews");
    if (!Array.isArray(data)) throw payloadShapeError();
    setItems(data);
  }, []);
  // BUG-051's class, on this screen too: the failure was a TOAST, which lives about four
  // seconds, and what stayed on screen afterwards was the empty state — "no answers to
  // review" — a claim about the practice made from a request that failed.
  const { loading, loadError, reload } = useAdminLoad(load);

  const mark = async (item, status) => {
    try {
      await api.post(reviewTarget(item).markPath, { status });
    } catch (e) {
      toast.error(extractApiError(e, t("qr.error"), tv));
      return;
    }
    toast.success(t("qr.marked", { status: t(STATUS_KEY[status]) }));
    // The decision above already landed — a failed refresh is a STALE LIST, not a failed
    // review, and reporting it as one invites the practitioner to mark the answer twice.
    try {
      await load();
    } catch (e) {
      toast.error(extractApiError(e, t("toast.reloadFailed"), tv));
    }
  };

  const filtered = items.filter((i) => filter === "all" || i.review_status === filter);

  return (
    <div data-testid="quiz-reviews-admin" className="space-y-6">
      <div>
        <h1 className="font-display text-3xl text-eh-ink flex items-center gap-3">
          <FileQuestion className="w-6 h-6 text-sol-gold" /> {t("qr.title")}
        </h1>
        <p className="text-eh-muted text-sm mt-1">
          {t("qr.subtitle", {
            pending: items.filter((i) => i.review_status === "pending_review").length,
            approved: items.filter((i) => i.review_status === "approved").length,
            rejected: items.filter((i) => i.review_status === "rejected").length,
          })}
        </p>
      </div>

      <div className="flex gap-2">
        {["pending_review", "approved", "rejected", "all"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`review-filter-${f}`}
            className={`px-4 h-9 rounded-full text-xs transition ${
              filter === f ? "bg-eh-ink text-eh-bg" : "bg-white border border-eh-border-soft text-eh-muted hover:bg-eh-cream/30"
            }`}
          >
            {f === "all" ? t("qr.filterAll") : t(STATUS_KEY[f])}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-eh-muted" /></div>
      ) : loadError ? (
        <LoadFailure
          message={extractApiError(loadError, tv("errors.loadFailed"), tv)}
          onRetry={reload}
          retryLabel={tv("actions.retry")}
          testId="admin-quiz-reviews-load-error"
          retryTestId="admin-quiz-reviews-retry"
          className="py-16"
        />
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-eh-muted bg-white rounded-2xl border border-eh-border-soft" data-testid={`review-empty-${filter}`}>
          <FileQuestion className="w-12 h-12 mx-auto mb-3 opacity-40" />
          {t(EMPTY_KEY[filter])}
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((item) => (
            <ReviewCard key={reviewTarget(item).key} item={item} onMark={mark} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewCard({ item, onMark, t }) {
  const target = reviewTarget(item);
  return (
    <div className="bg-white rounded-2xl border border-eh-border-soft p-6" data-testid={`review-${item.enrollment_id}`}>
      <div className="flex items-start justify-between mb-4 gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${STATUS_STYLE[item.review_status] || STATUS_STYLE.pending_review}`}>
              {t(STATUS_KEY[item.review_status] || "qr.statusPending")}
            </span>
            <span className="text-xs text-eh-muted">{t("qr.mcqScore", { score: item.score, result: item.passed ? t("qr.passed") : t("qr.failed") })}</span>
            {item.submitted_at && (
              <span className="text-xs text-eh-muted inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> {new Date(item.submitted_at).toLocaleString()}
              </span>
            )}
          </div>
          <h3 className="font-display text-xl text-eh-ink">{target.heading}</h3>
          <div className="text-sm text-eh-muted">
            {t(target.partLabelKey)} <strong>{target.partTitle}</strong> · {t("qr.student")} <a href={`mailto:${item.student_email}`} className="underline hover:text-eh-ink">{item.student_email}</a>
          </div>
        </div>
        {item.review_status === "pending_review" && (
          <div className="flex gap-2 flex-shrink-0">
            <Button size="sm" onClick={() => onMark(item, "approved")} data-testid={`approve-${item.enrollment_id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <CheckCircle2 className="w-3 h-3 mr-1" /> {t("qr.approve")}
            </Button>
            <Button size="sm" variant="outline" onClick={() => onMark(item, "rejected")} data-testid={`reject-${item.enrollment_id}`}>
              <X className="w-3 h-3 mr-1" /> {t("qr.reject")}
            </Button>
          </div>
        )}
      </div>

      <div className="space-y-3 mt-4">
        {item.questions.map((q) => {
          const answer = item.open_answers[q.id] || "";
          return (
            <div key={q.id} className="bg-violet-50/40 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-violet-700 mb-1">{t("qr.question")}</div>
              <div className="font-medium text-eh-ink mb-3">{q.question}</div>
              <div className="text-xs uppercase tracking-wider text-violet-700 mb-1">{t("qr.studentAnswer")}</div>
              <div className="text-sm text-eh-ink whitespace-pre-wrap bg-white rounded p-3 border border-violet-100">
                {answer || <em className="text-eh-muted">{t("qr.noAnswer")}</em>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
